import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Container,
  Paper,
  Divider,
  Grid,
  Snackbar,
  Alert,
  InputAdornment,
} from '@mui/material'
import { handleAdminError } from '../utils/errorHandler'
// import { useDataProvider } from 'react-admin';
import { QRCodeSVG } from 'qrcode.react'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import StyledTooltip from '../components/StyledTooltip'

const PROJECT_NAME = 'Starter Project'
// eslint-disable-next-line no-useless-escape
const passwordPattern =
  /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[`~!@#$%^&*()\-_=+\[\]{}\\|;:'",<.>\/\?€£¥₹])([A-Za-z0-9`~!@#$%^&*()\-_=+\[\]{}\\|;:'",<.>\/\?€£¥₹]+)$/

const extractSecretFromURI = (uri) => {
  const queryParams = new URLSearchParams(uri.split('?')[1])
  return queryParams.get('secret')
}

const AdminInvitation = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [firstName, setFirstName] = useState('')
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [qrCodeData, setQrCodeData] = useState(null)
  const [step, setStep] = useState('form')
  const [showSecret, setShowSecret] = useState(false)
  const [timer, setTimer] = useState(0)
  const [email, setEmail] = useState('')
  const [success, setSuccess] = useState(false)
  // const dataProvider = useDataProvider();

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please request a new invitation.')
    } else {
      // Here will be API request to validate token
      // and get user email
      // For demonstration, we use mocked email
      setEmail('mockedEmail@gmail.com')
    }
  }, [token])

  useEffect(() => {
    if (step === 'qrcode') {
      setTimer(10)

      const countdown = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1)
      }, 1000)

      return () => clearInterval(countdown)
    }
  }, [step])

  useEffect(() => {
    if (timer <= 0) {
      clearInterval(timer)
    }
  }, [timer])

  const handleSubmit = async (e) => {
    e.preventDefault()

    setLoading(true)
    setError(null)

    try {
      const response = await mockApiCall({
        firstName,
        password,
        token,
      })

      setQrCodeData(response.qrCodeData)
      setStep('qrcode')
    } catch (err) {
      setError(handleAdminError(err))
    } finally {
      setLoading(false)
    }
  }

  const completeRegistration = async () => {
    setLoading(true)
    try {
      // Here will be API request to complete registration
      await new Promise((resolve) => setTimeout(resolve, 1000))

      setSuccess(true)
      setTimeout(() => {
        window.location.href = '/#/'
      }, 2000)
    } catch (error) {
      setError('Error completing registration. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const mockApiCall = async (data) => {
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // Mocked response with QR code data
    return {
      qrCodeData: `otpauth://totp/Project-Admin:${data.firstName} ?secret=JBSWY3DPEHPK3PXP&issuer=A2-Admin`,
    }
  }

  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      {email && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          You are registering with email: <strong>{email}</strong>
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Name"
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Password"
            fullWidth
            value={password}
            type="password"
            onChange={(e) => {
              const value = e.target.value
              setPassword(value)
              if (value !== '' && !passwordPattern.test(value)) {
                setPasswordError(
                  'Password must include at least one number, one lowercase letter, one uppercase letter and one special character.'
                )
              } else {
                setPasswordError('')
              }
            }}
            error={!!passwordError}
            InputProps={{
              endAdornment: passwordError ? (
                <InputAdornment position="end">
                  <StyledTooltip title={passwordError}>
                    <ErrorOutlineIcon color="error" />
                  </StyledTooltip>
                </InputAdornment>
              ) : null,
            }}
            required
          />
        </Grid>
      </Grid>

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 3 }}
        disabled={loading || !passwordPattern.test(password)}
      >
        {loading ? <CircularProgress size={24} /> : 'Register & Setup 2FA'}
      </Button>
    </form>
  )

  const renderQRCode = () => (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Typography variant="h6" gutterBottom>
        Set up Two-Factor Authentication
      </Typography>

      <Typography variant="body1" sx={{ mb: 2 }}>
        Scan this QR code with your authenticator app (like Google Authenticator or Authy).
      </Typography>

      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          bgcolor: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: 'fit-content',
        }}
      >
        <QRCodeSVG value={qrCodeData} size={200} />
      </Paper>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Or you can enter the secret code manually in your authenticator app:
      </Typography>

      <Button
        variant="outlined"
        color="primary"
        onClick={() => setShowSecret(!showSecret)}
        sx={{ mb: 2 }}
      >
        {showSecret ? 'Hide Secret Code' : 'Show Secret Code'}
      </Button>

      {showSecret && qrCodeData && (
        <Paper
          elevation={2}
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            fontFamily: 'monospace',
            width: 'fit-content',
            textAlign: 'center',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Secret Code:
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'monospace',
              letterSpacing: '0.1em',
            }}
          >
            {extractSecretFromURI(qrCodeData)}
          </Typography>
        </Paper>
      )}

      <Divider sx={{ width: '100%', my: 2 }} />

      <Typography paragraph>
        After scanning the QR code, you'll need to enter the code from your authenticator app
        when logging in to the admin panel.
      </Typography>

      <Button
        variant="contained"
        color="primary"
        onClick={completeRegistration}
        disabled={timer > 0 || loading}
        sx={{ mt: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : `Ok${timer > 0 ? ` (${timer}s)` : ''}`}
      </Button>
    </Box>
  )

  return (
    <Container
      maxWidth="sm"
      fullWidth
      style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom fontWeight="500">
            {PROJECT_NAME} Admin Registration
          </Typography>

          {!token && (
            <Typography color="error" variant="body1" align="center">
              Invalid invitation link. Please request a new invitation.
            </Typography>
          )}

          {token && step === 'form' && renderForm()}
          {token && step === 'qrcode' && renderQRCode()}
        </CardContent>
      </Card>
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Registration completed successfully! Redirecting to login page...
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default AdminInvitation
